// src/pages/LoginPage.js

import React, { useState, useEffect } from "react";
import useLogin from "../hooks/useLogin";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "../styles/LoginPage.css";
import { jwtDecode } from "jwt-decode";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { currentUser } = useAuth();
  const {
    handleLogin,
    handleForgotPassword,
    handleGoogleSignIn,
    getRedirectUrlAfterLogin,
  } = useLogin();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jwtFromUrl = params.get("jwt");
    if (jwtFromUrl) {
      try {
        const decoded = jwtDecode(jwtFromUrl);
        if (decoded.exp && decoded.exp * 1000 > Date.now()) {
          // استقبال jwt وتخزينه
        } else {
          setErrorMessage(
            "انتهت صلاحية رابط التفعيل. يرجى تسجيل الدخول مجدداً أو إعادة طلب رابط.",
          );
        }
      } catch (err) {
        setErrorMessage("رمز الدخول غير صالح.");
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      currentUser &&
      currentUser.role &&
      currentUser.role.toLowerCase() === "member"
    ) {
      navigate("/member-dashboard");
    } else if (currentUser && currentUser.role) {
      const role = currentUser.role.toLowerCase();
      if (["admin", "emp", "manager"].includes(role)) {
        navigate("/dashboard");
      } else {
        navigate("/unauthorized");
      }
    }
    // eslint-disable-next-line
  }, [currentUser, navigate]);

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!email) {
      setErrorMessage("يرجى إدخال البريد الإلكتروني.");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage(
        "البريد الإلكتروني غير صالح. يرجى التحقق من البريد الإلكتروني المدخل.",
      );
      return;
    }

    if (!isForgotPassword && !password) {
      setErrorMessage("يرجى إدخال كلمة المرور.");
      return;
    }

    setIsLoading(true);

    try {
      if (isForgotPassword) {
        await handleForgotPassword(email);
        setSuccessMessage("تم إرسال رابط استعادة كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد.");
        setErrorMessage("");
        setIsForgotPassword(false);
      } else {
        await handleLogin(email, password);
      }
    } catch (error) {
      setErrorMessage(error.message || "حدث خطأ أثناء تسجيل الدخول.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = () => {
    if (errorMessage) {
      setErrorMessage("");
    }
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const onGoogleSignIn = async () => {
    setErrorMessage("ميزة تسجيل الدخول بجوجل غير مدعومة في الوقت الحالي.");
  };

  return (
    <div className="login-container">
      <div className="login-page">
        <div
          className={`login-box ${
            isForgotPassword ? "forgot-password-mode" : ""
          }`}
        >
          {/* Logo Section */}
          <div className="logo-section">
            <img
              src={process.env.PUBLIC_URL + "/matajer.png"}
              alt="Matajer Logo"
              className="app-logo"
            />
          </div>

          {/* Header */}
          <div className="login-header">
            <h1 className="page-title">
              {isForgotPassword ? "استعادة كلمة المرور" : "تسجيل الدخول"}
            </h1>
            <p className="page-subtitle">
              {isForgotPassword
                ? "أدخل بريدك الإلكتروني لاستعادة كلمة المرور"
                : "مرحباً بعودتك! قم بتسجيل الدخول للمتابعة"}
            </p>
          </div>

          {/* Messages */}
          {successMessage && (
            <div className="alert-box alert-success">
              <svg
                className="alert-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{successMessage}</span>
            </div>
          )}
          {errorMessage && (
            <div className="alert-box alert-error">
              <svg
                className="alert-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{errorMessage}</span>
            </div>
          )}
          {redirectLoading && (
            <div className="alert-box alert-info">
              <span className="loading-spinner-sm"></span>
              <span>جاري تحويلك للنسخة الأصلية...</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={onSubmit} className="login-form">
            <div className="form-group">
              <label>البريد الإلكتروني</label>
              <div className="input-wrapper">
                <svg
                  className="input-icon"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <input
                  type="email"
                  className={`form-control ${
                    errorMessage && !validateEmail(email) ? "error" : ""
                  }`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    handleInputChange();
                  }}
                  placeholder="example@email.com"
                  dir="ltr"
                  required
                />
              </div>
            </div>

            {!isForgotPassword && (
              <div className="form-group">
                <label>كلمة المرور</label>
                <div className="input-wrapper">
                  <svg
                    className="input-icon"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      handleInputChange();
                    }}
                    placeholder="أدخل كلمة المرور"
                    dir="ltr"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="toggle-password-btn"
                    aria-label={
                      showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"
                    }
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot Password Link - placed under password for better UX */}
            {!isForgotPassword && (
              <div className="forgot-row">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="btn-link-inline"
                >
                  نسيت كلمة المرور؟
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              {isForgotPassword ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      جاري الإرسال...
                    </>
                  ) : (
                    "إرسال رابط الاستعادة"
                  )}
                </button>
              ) : (
                <>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="loading-spinner"></span>
                        جاري تسجيل الدخول...
                      </>
                    ) : (
                      "تسجيل الدخول"
                    )}
                  </button>

                  <div className="separator">
                    <span>أو</span>
                  </div>

                  <button
                    type="button"
                    onClick={onGoogleSignIn}
                    className="btn btn-google"
                    disabled
                  >
                    <svg className="google-icon" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    تسجيل الدخول باستخدام Google
                  </button>
                </>
              )}
            </div>

            {/* Footer Links */}
            <div className="form-footer-links">
              {!isForgotPassword && (
                <div className="signup-prompt">
                  <span>ليس لديك حساب؟</span>
                  <button
                    type="button"
                    onClick={() => navigate("/member-registration")}
                    className="btn-link-accent"
                  >
                    سجّل الآن
                  </button>
                </div>
              )}
              {isForgotPassword && (
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="btn-link-back"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="back-arrow"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  العودة إلى تسجيل الدخول
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
